import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAvailableLocales } from "../../hooks/data-review";
import { LocaleService } from "../../services/locale";
import { RealmsService, type Realm } from "../../services/realms";
import type { User } from "../../types/auth";
import { Dropdown, Icon, TreelLogo } from "../atoms";

interface NavigationTab {
  id: string;
  label: string;
  path: string;
  icon?: React.ReactNode;
  badge?: number;
}

interface TopNavigationProps {
  tabs: NavigationTab[];
  user: User | null;
  onLogout: () => void;
  className?: string;
}

export function TopNavigation({
  tabs,
  user,
  onLogout,
  className = "",
}: TopNavigationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const [realms, setRealms] = useState<Realm[]>([]);
  const [selectedRealmId, setSelectedRealmId] = useState<number | null>(null);
  const [loadingRealms, setLoadingRealms] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState<string>("");
  const { data: availableLocales, isLoading: loadingLocales } = useAvailableLocales(selectedRealmId ?? undefined);

  const handleTabClick = (tab: NavigationTab) => {
    navigate(tab.path);
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
  };

  const handleProfileClick = () => {
    navigate("/profile");
    setIsProfileDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const isActiveTab = (tab: NavigationTab) =>
    location.pathname === tab.path ||
    (tab.path !== "/" && location.pathname.startsWith(tab.path));

  // Load realms if user is treel-admin
  useEffect(() => {
    if (user?.realmRole === "treel-admin") {
      void loadRealms();
    }
  }, [user?.realmRole]);

  // Initialize selected realm from localStorage or set first realm as default
  useEffect(() => {
    // const storedRealmId = RealmsService.getSelectedRealm();

    //   if (storedRealmId) {
    //     setSelectedRealmId(storedRealmId);
    //   } else if (user?.realmId) {
    //     setSelectedRealmId(user.realmId);
    //   } else if (realms && realms.length > 0) {
    //     // Fallback to first realm if user doesn't have realmId
    //     const firstRealm = realms.at(0);
    //     if (firstRealm) {
    //       setSelectedRealmId(firstRealm.id);
    //     }
    //   }

    const storedRealmId = RealmsService.getSelectedRealm();
    if (storedRealmId) {
      setSelectedRealmId(storedRealmId);
    } else if (realms.length > 0 && !selectedRealmId) {
      // Set first realm as default if no stored selection
      const firstRealmId = realms[0].id;
      setSelectedRealmId(firstRealmId);
      RealmsService.setSelectedRealm(firstRealmId);
    }
  }, [realms, selectedRealmId]);

  const loadRealms = async () => {
    try {
      setLoadingRealms(true);
      const realmsData = await RealmsService.getRealms();
      setRealms(realmsData);
    } catch (err) {
      console.error("Failed to load realms:", err);
    } finally {
      setLoadingRealms(false);
    }
  };

  const handleRealmChange = (realmId: number | null) => {
    // Always require a realm to be selected
    if (realmId) {
      RealmsService.setSelectedRealm(realmId);
      setSelectedRealmId(realmId);
      // Reload the page to apply the new realm context
      window.location.reload();
    }
  };

  const handleLocaleChange = (locale: string | null) => {
    if (locale) {
      LocaleService.setSelectedLocale(locale);
      setSelectedLocale(locale);
      // Reload the page to apply the new locale context
      window.location.reload();
    }
  };

  const formatLocaleName = (locale: string) => {
    try {
      const countryNameConverter = new Intl.DisplayNames(["en"], { type: "region" });
      const langNameConverter = new Intl.DisplayNames(["en"], { type: "language" });
      const [lang, country] = locale.split("-");

      if (lang === "hans") {
        return `${locale} - Chinese Simplified (${countryNameConverter.of(country)})`;
      }
      if (lang === "hant") {
        return `${locale} - Chinese Traditional (${countryNameConverter.of(country)})`;
      }

      return `${locale} - ${langNameConverter.of(lang.replace("hant", "zh").replace("hans", "zh"))} (${countryNameConverter.of(country)})`;
    } catch {
      return locale;
    }
  };

  // Initialize selected locale from localStorage or set first available locale
  useEffect(() => {
    const storedLocale = LocaleService.getSelectedLocale();
    if (storedLocale) {
      setSelectedLocale(storedLocale);
    } else if (availableLocales && availableLocales.length > 0 && !selectedLocale) {
      // Set first locale as default if no stored selection
      const firstLocale = availableLocales.at(0);
      if (firstLocale) {
        setSelectedLocale(firstLocale);
        LocaleService.setSelectedLocale(firstLocale);
      }
    }
  }, [availableLocales, selectedLocale]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  return (
    <nav
      className={`sticky top-0 z-50 backdrop-blur-md bg-bg-secondary/95 border-b border-border-light shadow-sm ${className}`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center min-h-[4.5rem] py-3">
          {/* Left section - App branding */}
          <div className="flex items-center flex-shrink-0">
            <div className="flex items-center space-x-3">
              <TreelLogo size="md" />
              <div className="flex flex-col">
                <h1 className="text-lg lg:text-xl font-bold text-text-primary leading-tight">
                  Treel Coach
                </h1>
                <span className="hidden xl:block text-xs text-text-muted font-medium">
                  AI Playground
                </span>
              </div>
            </div>
          </div>

          {/* Center section - Navigation Tabs */}
          <div className="flex-1 flex justify-center mx-4">
            {/* Desktop Navigation */}
            <div className="hidden lg:flex">
              <div className="flex space-x-1 bg-white/40 rounded-xl p-1.5 backdrop-blur-sm border border-white/30 shadow-sm">
                {tabs.map((tab) => {
                  const isActive = isActiveTab(tab);

                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabClick(tab)}
                      className={`
                        relative px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200
                        flex items-center space-x-2 focus:outline-none group min-w-0
                        ${
                          isActive
                            ? "bg-action-key-default text-white shadow-md"
                            : "text-text-secondary hover:text-text-primary hover:bg-white/60"
                        }
                      `}
                      type="button"
                    >
                      {tab.icon && (
                        <span
                          className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isActive ? "scale-110" : "group-hover:scale-110"}`}
                        >
                          {tab.icon}
                        </span>
                      )}
                      <span className="font-medium whitespace-nowrap">
                        {tab.label}
                      </span>
                      {tab.badge && tab.badge > 0 && (
                        <span className="ml-1 bg-white text-action-key-default text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] h-5 flex items-center justify-center font-bold">
                          {tab.badge > 99 ? "99+" : tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Medium screens Navigation */}
            <div className="hidden md:flex lg:hidden">
              <div className="flex space-x-1 bg-white/40 rounded-lg p-1 backdrop-blur-sm border border-white/30 shadow-sm">
                {tabs.slice(0, 4).map((tab) => {
                  const isActive = isActiveTab(tab);

                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabClick(tab)}
                      className={`
                        relative p-2.5 rounded-md transition-all duration-200
                        flex items-center justify-center focus:outline-none group
                        ${
                          isActive
                            ? "bg-action-key-default text-white shadow-sm"
                            : "text-text-secondary hover:text-text-primary hover:bg-white/60"
                        }
                      `}
                      type="button"
                      title={tab.label}
                    >
                      <span
                        className={`w-4 h-4 transition-transform duration-200 ${isActive ? "scale-110" : "group-hover:scale-105"}`}
                      >
                        {tab.icon}
                      </span>
                      {tab.badge && tab.badge > 0 && (
                        <span className="absolute -top-1 -right-1 bg-action-key-default text-white text-xs rounded-full px-1 py-0.5 min-w-[0.875rem] h-3.5 flex items-center justify-center font-bold text-[10px]">
                          {tab.badge > 9 ? "9+" : tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Realm Selector - Only show for treel-admin users */}
          {user?.realmRole === "treel-admin" && (
            <div className="mr-3">
              <Dropdown
                value={selectedRealmId}
                options={realms.map((realm) => ({
                  value: realm.id,
                  label: realm.name,
                }))}
                onChange={(value) => handleRealmChange(value as number | null)}
                placeholder="Select Realm"
                disabled={false}
                loading={loadingRealms}
                className="min-w-[150px]"
              />
            </div>
          )}

          {/* Locale Selector - Show for all admin users */}
          {(user?.realmRole === "admin" || user?.realmRole === "treel-admin") && availableLocales && availableLocales.length > 0 && (
            <div className="mr-3">
              <Dropdown
                value={selectedLocale}
                options={availableLocales.map((locale) => ({
                  value: locale,
                  label: formatLocaleName(locale),
                }))}
                onChange={(value) => handleLocaleChange(value as string | null)}
                placeholder="Select Locale"
                disabled={false}
                loading={loadingLocales}
                className="min-w-[200px]"
              />
            </div>
          )}

          {/* Right section - User profile */}
          <div className="flex items-center flex-shrink-0 justify-end">
            {user ? (
              <div className="relative" ref={profileDropdownRef}>
                {/* Compact user avatar button */}
                <button
                  onClick={() =>
                    setIsProfileDropdownOpen(!isProfileDropdownOpen)
                  }
                  className="flex items-center justify-center w-9 h-9 bg-white/50 backdrop-blur-sm border border-white/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:bg-white/70"
                  title={`${user.name ?? (user.firstName ? `${user.firstName} ${user.lastName ?? ""}`.trim() : user.email)} - Click for menu`}
                >
                  <div className="w-6 h-6 rounded-full bg-action-key-default flex items-center justify-center text-white font-bold text-xs">
                    {(user.name ?? user.firstName ?? user.email ?? "")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                </button>

                {/* Profile Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-md border border-white/30 rounded-lg shadow-lg overflow-hidden z-50">
                    <div className="py-1">
                      {/* User info header */}
                      <div className="px-3 py-2 border-b border-border-light bg-bg-surface">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-action-key-default flex items-center justify-center text-white font-bold text-sm">
                            {(user.name ?? user.firstName ?? user.email ?? "")
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-sm font-medium text-text-primary truncate">
                              {user.name ??
                                (user.firstName
                                  ? `${user.firstName} ${user.lastName ?? ""}`.trim()
                                  : "User")}
                            </span>
                            <span className="text-xs text-text-muted truncate">
                              {user.email}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Menu items */}
                      <div className="py-1">
                        <button
                          onClick={handleProfileClick}
                          className="w-full flex items-center space-x-2 px-3 py-2.5 text-sm text-text-primary hover:bg-white/60 transition-all duration-200"
                        >
                          <Icon name="user" size="sm" color="secondary" />
                          <span className="font-medium">Profile Settings</span>
                        </button>

                        <button
                          onClick={() => {
                            navigate("/settings");
                            setIsProfileDropdownOpen(false);
                          }}
                          className="w-full flex items-center space-x-2 px-3 py-2.5 text-sm text-text-primary hover:bg-white/60 transition-all duration-200"
                        >
                          <Icon name="settings" size="sm" color="secondary" />
                          <span className="font-medium">App Settings</span>
                        </button>

                        <div className="border-t border-border-light my-1" />

                        <button
                          onClick={() => {
                            onLogout();
                            setIsProfileDropdownOpen(false);
                          }}
                          className="w-full flex items-center space-x-2 px-3 py-2.5 text-sm text-danger hover:bg-danger-light/50 transition-all duration-200 font-medium"
                        >
                          <Icon name="logout" size="sm" color="danger" />
                          <span className="font-medium">Sign Out</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Mobile Menu Button for non-authenticated users */
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-9 h-9 rounded-lg bg-white/60 backdrop-blur-sm border border-white/30 text-text-secondary hover:text-text-primary hover:bg-white/80 focus:outline-none transition-all duration-200 flex items-center justify-center"
                type="button"
              >
                <Icon
                  name="menu"
                  size="md"
                  className={`transition-transform duration-200 ${isMobileMenuOpen ? "rotate-90" : ""}`}
                />
              </button>
            )}
          </div>

          {/* Mobile Menu Button for authenticated users */}
          {user && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden ml-2 w-9 h-9 rounded-lg bg-white/60 backdrop-blur-sm border border-white/30 text-text-secondary hover:text-text-primary hover:bg-white/80 focus:outline-none transition-all duration-200 flex items-center justify-center"
              type="button"
            >
              <Icon
                name="menu"
                size="md"
                className={`transition-transform duration-200 ${isMobileMenuOpen ? "rotate-90" : ""}`}
              />
            </button>
          )}
        </div>

        {/* Enhanced Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/20 backdrop-blur-md bg-white/90 shadow-lg">
            <div className="px-6 py-6 space-y-3">
              {/* User info on mobile */}
              {user && (
                <div className="mb-6">
                  {/* Realm Selector on Mobile - Only show for treel-admin users */}
                  {user.realmRole === "treel-admin" && (
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-text-muted mb-1 px-1">
                        Select Realm
                      </label>
                      <Dropdown
                        value={selectedRealmId}
                        options={realms.map((realm) => ({
                          value: realm.id,
                          label: realm.name,
                        }))}
                        onChange={(value) =>
                          handleRealmChange(value as number | null)
                        }
                        placeholder="Select Realm"
                        disabled={false}
                        loading={loadingRealms}
                        className="w-full"
                      />
                    </div>
                  )}

                  {/* Locale Selector on Mobile - Show for all admin users */}
                  {(user.realmRole === "admin" || user.realmRole === "treel-admin") && availableLocales && availableLocales.length > 0 && (
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-text-muted mb-1 px-1">
                        Select Locale
                      </label>
                      <Dropdown
                        value={selectedLocale}
                        options={availableLocales.map((locale) => ({
                          value: locale,
                          label: formatLocaleName(locale),
                        }))}
                        onChange={(value) =>
                          handleLocaleChange(value as string | null)
                        }
                        placeholder="Select Locale"
                        disabled={false}
                        loading={loadingLocales}
                        className="w-full"
                      />
                    </div>
                  )}

                  <div className="flex items-center space-x-3 px-4 py-3 rounded-xl bg-gradient-to-r from-[#FFF7FC] to-[#FFF9F2] border border-white/40 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-action-key-default to-pressed-tomato flex items-center justify-center text-white font-bold shadow-md">
                      {(user.name ?? user.firstName ?? user.email ?? "")
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-semibold text-text-primary truncate">
                        {user.name ?? user.email}
                      </span>
                      <span className="text-xs text-text-muted font-medium">
                        Coach User
                      </span>
                    </div>
                  </div>

                  {/* Profile quick actions on mobile */}
                  <div className="flex space-x-2">
                    <button
                      onClick={handleProfileClick}
                      className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-white/60 hover:bg-white/80 rounded-lg text-sm font-medium text-text-primary transition-all duration-200"
                    >
                      <Icon name="user" size="sm" />
                      <span>Profile</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate("/settings");
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-white/60 hover:bg-white/80 rounded-lg text-sm font-medium text-text-primary transition-all duration-200"
                    >
                      <Icon name="settings" size="sm" />
                      <span>Settings</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Navigation items */}
              {tabs.map((tab) => {
                const isActive = isActiveTab(tab);

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab)}
                    className={`
                      w-full flex items-center space-x-4 px-5 py-4 rounded-xl text-sm font-semibold transition-all duration-300 relative
                      ${
                        isActive
                          ? "bg-gradient-to-r from-action-key-default to-pressed-tomato text-white shadow-lg"
                          : "text-text-secondary hover:text-text-primary hover:bg-white/60 hover:shadow-md"
                      }
                    `}
                    type="button"
                  >
                    {tab.icon && (
                      <span
                        className={`w-6 h-6 flex-shrink-0 transition-transform duration-300 ${isActive ? "scale-110" : ""}`}
                      >
                        {tab.icon}
                      </span>
                    )}
                    <span className="flex-1 text-left">{tab.label}</span>
                    {tab.badge && tab.badge > 0 && (
                      <span className="bg-white text-action-key-default text-xs rounded-full px-2.5 py-1 min-w-[1.5rem] h-6 flex items-center justify-center font-bold shadow-sm">
                        {tab.badge > 99 ? "99+" : tab.badge}
                      </span>
                    )}

                    {/* Mobile active indicator */}
                    {isActive && (
                      <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1.5 h-8 bg-white rounded-r-full shadow-lg" />
                    )}

                    {/* Arrow indicator for active */}
                    {isActive && (
                      <Icon name="arrow-right" size="sm" className="ml-2" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
